import { useState, useEffect } from 'react'
import { usePageTitle } from '../../hooks/usePageTitle'
import { supabase } from '../../lib/supabase'
import {
  VStack,
  HStack,
  Card,
  PageHeader,
  Text,
  Button,
  Badge,
  Table,
  Banner,
  Animate,
  EmptyState,
  Input,
  Modal
} from 'wss3-forge'
import {
  Add20Regular,
  Building20Regular,
  People20Regular,
  Open20Regular,
  Shield20Regular
} from '@fluentui/react-icons'

interface MyOrganization {
  id: string
  name: string
  slug: string
  logo_url: string | null
  is_active: boolean
  role: string
  is_admin: boolean
  members_count: number
  created_at: string
}

function getRoleBadgeVariant(role: string): 'primary' | 'success' | 'warning' | 'default' {
  switch (role) {
    case 'owner': return 'primary'
    case 'admin': return 'success'
    case 'sso_admin': return 'warning'
    default: return 'default'
  }
}

function getRoleLabel(role: string): string {
  switch (role) {
    case 'owner': return 'Owner'
    case 'admin': return 'Admin'
    case 'sso_admin': return 'SSO Admin'
    case 'viewer': return 'Viewer'
    case 'member': return 'Member'
    default: return role
  }
}

export function MyOrganizations() {
  usePageTitle('My Organizations')
  const [organizations, setOrganizations] = useState<MyOrganization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')
  const [newOrgSlug, setNewOrgSlug] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadOrganizations()
  }, [])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      const { data, error: fetchError } = await supabase.rpc('get_my_organizations')

      if (fetchError) throw fetchError
      setOrganizations(data || [])
    } catch (err: any) {
      console.error('Error loading organizations:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenOrg = (org: MyOrganization) => {
    if (org.is_admin) {
      window.open(`/org/${org.slug}`, '_blank')
    }
  }

  const handleCreateOrg = async () => {
    if (!newOrgName.trim() || !newOrgSlug.trim()) {
      setError('Name and slug are required')
      return
    }

    // Validate slug format
    const slugRegex = /^[a-z0-9-]+$/
    if (!slugRegex.test(newOrgSlug)) {
      setError('Slug must only contain lowercase letters, numbers, and hyphens')
      return
    }

    setCreating(true)
    setError(null)

    try {
      const { data, error: createError } = await supabase.rpc('create_organization', {
        p_name: newOrgName.trim(),
        p_slug: newOrgSlug.trim()
      })

      if (createError) throw createError

      setSuccess('Organization created successfully!')
      setShowCreateModal(false)
      setNewOrgName('')
      setNewOrgSlug('')
      loadOrganizations()

      // Open the new org in a new tab
      if (data?.slug) {
        window.open(`/org/${data.slug}`, '_blank')
      }
    } catch (err: any) {
      console.error('Error creating organization:', err)
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  return (
    <div className="page-content">
      <VStack gap="lg">
        <Animate type="fadeIn">
          <PageHeader
            title="My Organizations"
            subtitle="Organizations you belong to"
            actions={
              <Button icon={<Add20Regular />} onClick={() => setShowCreateModal(true)}>
                Create Organization
              </Button>
            }
          />
        </Animate>

        {error && (
          <Banner variant="error" title="Error" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        {success && (
          <Banner variant="success" title="Success" onDismiss={() => setSuccess(null)}>
            {success}
          </Banner>
        )}

        <Animate type="slideInUp" delay={100}>
          {!loading && organizations.length === 0 ? (
            <Card padding="xl">
              <EmptyState
                icon={<Building20Regular />}
                title="No organizations yet"
                description="You're not a member of any organization. Create one to get started with enterprise SSO."
                action={{
                  label: 'Create Organization',
                  onClick: () => setShowCreateModal(true)
                }}
              />
            </Card>
          ) : (
            <Table
              columns={[
                {
                  key: 'name',
                  header: 'Organization',
                  render: (_, row: MyOrganization) => (
                    <HStack gap="md" style={{ alignItems: 'center' }}>
                      {row.logo_url ? (
                        <img
                          src={row.logo_url}
                          alt={row.name}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-sm)',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: 'var(--radius-sm)',
                          backgroundColor: 'var(--brand-primary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          {row.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <VStack gap="none">
                        <Text weight="medium">{row.name}</Text>
                        <Text size="sm" color="muted">{row.slug}</Text>
                      </VStack>
                    </HStack>
                  )
                },
                {
                  key: 'role',
                  header: 'Your Role',
                  render: (_, row: MyOrganization) => (
                    <Badge variant={getRoleBadgeVariant(row.role)}>
                      {getRoleLabel(row.role)}
                    </Badge>
                  )
                },
                {
                  key: 'members',
                  header: 'Members',
                  render: (_, row: MyOrganization) => (
                    <HStack gap="xs" style={{ alignItems: 'center' }}>
                      <People20Regular style={{ color: 'var(--text-muted)' }} />
                      <Text>{row.members_count}</Text>
                    </HStack>
                  )
                },
                {
                  key: 'access',
                  header: 'Admin Access',
                  render: (_, row: MyOrganization) => (
                    row.is_admin ? (
                      <HStack gap="xs" style={{ alignItems: 'center' }}>
                        <Shield20Regular style={{ color: 'var(--status-success)' }} />
                        <Text size="sm" style={{ color: 'var(--status-success)' }}>Yes</Text>
                      </HStack>
                    ) : (
                      <Text size="sm" color="muted">No</Text>
                    )
                  )
                },
                {
                  key: 'actions',
                  header: '',
                  render: (_, row: MyOrganization) => (
                    row.is_admin && (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<Open20Regular />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenOrg(row)
                        }}
                      >
                        Open Portal
                      </Button>
                    )
                  )
                }
              ]}
              data={organizations}
              loading={loading}
              onRowClick={(row) => row.is_admin && handleOpenOrg(row)}
            />
          )}
        </Animate>
      </VStack>

      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Organization"
      >
        <VStack gap="md">
          <Text color="muted">
            Create a new organization to manage enterprise SSO for your company.
          </Text>

          <Input
            label="Organization Name"
            placeholder="My Company"
            value={newOrgName}
            onChange={(v) => {
              setNewOrgName(v as string)
              if (!newOrgSlug || newOrgSlug === generateSlug(newOrgName)) {
                setNewOrgSlug(generateSlug(v as string))
              }
            }}
          />

          <Input
            label="Slug"
            placeholder="my-company"
            value={newOrgSlug}
            onChange={(v) => setNewOrgSlug((v as string).toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            hint="Used in the URL: /org/my-company"
          />

          <HStack gap="sm" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrg} loading={creating}>
              Create
            </Button>
          </HStack>
        </VStack>
      </Modal>
    </div>
  )
}
